import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { BodyText, theme, VStack } from '@sg/garnish';

import { telemetryProductFromInput, useTelemetry } from '@order/Telemetry';

import { type PartialLineItem, type PartialProduct } from '../../../../graphql';
import { useNavigateToCustomizationEditScreen } from '../../hooks';
import { BagLineItem } from './BagLineItem';
import { useFormattedBagLineItems } from './hooks';

/**
 * This component is a list of {BagLineItem}.
 * It also binds the navigation callback for when a bag line item is pressed.
 */
export const BagLineItems = (props: BagLineItemsProps) => {
  const {
    lineItems = [],
    isGroupOrder,
    lineItemIdsBeingUpdated,
    restaurantSlug,
    addressId,
    isDelivery,
    isDisabled,
    trackLineItemPressed,
    startChangingLineItemQuantity,
    stopChangingLineItemQuantity,
    changeLineItemQuantity,
  } = props;

  const { formatMessage } = useIntl();
  const { track } = useTelemetry();

  const { navigateToCustomizationEditScreen } = useNavigateToCustomizationEditScreen({
    restaurantSlug,
    addressId,
    isDelivery,
  });

  // ─── Derived ──────────────────────────────────────────────────────────────

  const { lineItemGroups, shouldShowParticipantHeaders } = useFormattedBagLineItems({
    isGroupOrder,
    lineItems,
  });

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleOnPress = useCallback(
    (lineItemId: string, product: PartialProduct) => {
      trackLineItemPressed();
      track('pdp.view', {
        entryPoint: 'bag',
        selectedProduct: telemetryProductFromInput({
          ...product,
          location: [restaurantSlug, addressId].join(' | '),
        }),
      });

      navigateToCustomizationEditScreen({
        lineItemId,
        product,
      });
    },
    [restaurantSlug, addressId, track, trackLineItemPressed, navigateToCustomizationEditScreen],
  );

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <VStack gap={theme.spacing['4']} style={styles.container}>
      {lineItemGroups.map((lineItemGroup) => {
        const participantName = lineItemGroup.displayName || formatMessage(messages.guest);

        return (
          <VStack key={lineItemGroup.id} gap={0}>
            {shouldShowParticipantHeaders ? (
              <View style={styles.groupHeader}>
                <BodyText accessibilityRole="header" sizeMatch={['16']}>
                  {lineItemGroup.isMe ? (
                    <FormattedMessage {...messages.yourOrder} />
                  ) : (
                    <FormattedMessage
                      {...messages.participantOrder}
                      values={{ participant_name: participantName }}
                    />
                  )}
                </BodyText>
              </View>
            ) : null}

            <VStack hasDivider dividerColor={theme.colors.LIGHT} gap={theme.spacing['4']}>
              {lineItemGroup.lineItems.map((lineItem) => {
                const isUpdatingAnyItem = lineItemIdsBeingUpdated.length > 0;
                const isUpdatingThisItem = lineItemIdsBeingUpdated.includes(lineItem.id ?? '');
                const isLineItemReadOnly = isGroupOrder && !lineItem.canEdit;

                const shouldBlockInteraction =
                  isDisabled || isLineItemReadOnly || (isUpdatingAnyItem && !isUpdatingThisItem);

                return (
                  <View key={lineItem.id} style={styles.content}>
                    <BagLineItem
                      lineItem={lineItem}
                      isDisabled={shouldBlockInteraction}
                      startChangingLineItemQuantity={startChangingLineItemQuantity}
                      stopChangingLineItemQuantity={stopChangingLineItemQuantity}
                      changeLineItemQuantity={changeLineItemQuantity}
                      onPress={handleOnPress}
                    />
                  </View>
                );
              })}
            </VStack>
          </VStack>
        );
      })}
    </VStack>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  guest: {
    defaultMessage: 'Guest',
    description: 'Bag | Group order | Participant fallback name',
  },
  participantOrder: {
    defaultMessage: '{participant_name}’s order',
    description: 'Bag | Group order | Participant line items heading',
  },
  yourOrder: {
    defaultMessage: 'Your order',
    description: 'Bag | Group order | Current participant line items heading',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingVertical: theme.spacing['2'],
    marginHorizontal: -theme.spacing['4'],
    borderBottomColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderBottomWidth: theme.spacing['2'],
  },
  content: {
    paddingHorizontal: theme.spacing['2'],
  },
  groupHeader: {
    borderBottomColor: theme.colors.LIGHT,
    borderBottomWidth: 1,
    marginHorizontal: theme.spacing['4'],
    paddingTop: theme.spacing['3'],
    paddingBottom: theme.spacing['2'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type BagLineItemsProps = {
  lineItems?: readonly PartialLineItem[];
  isGroupOrder: boolean;
  lineItemIdsBeingUpdated: readonly string[];
  restaurantSlug?: string;
  addressId?: string;
  isDelivery?: boolean;
  isDisabled: boolean;
  trackLineItemPressed: () => void;
  startChangingLineItemQuantity: (lineItemId: string) => void;
  stopChangingLineItemQuantity: (lineItemId: string) => void;
  changeLineItemQuantity: (params: ChangeLineItemQuantityParams) => void;
};

type ChangeLineItemQuantityParams = {
  lineItemId: string;
  productId: string;
  quantity: number;
  resetQuantity?: () => void;
};
